import Head from "next/head";
import { useState, useContext, useMemo } from "react";
import { useTranslation } from "../../helpers/i18n";
import CommonHead from "../../builder/CommonHead";
import { signIn, SignInData } from "../../dataAccess/api/token";
import { useNavigateSameTab } from "../../helpers/navigation";
import { QueryParams, useQueryParams } from "../../dataAccess/QueryParams";
import LockScreen from "../ui/LockScreen";
import LoginForm from "../ui/LoginForm";
import { ServerError } from "../../dataAccess/ServerError";
import { FullHeightContainer } from "../ui/FullHeightContainer";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import { DEFAULT_SIGN_IN_VALUE } from "../../../theme/defaultConstants";
import Page from "../ui/Page";

export const availableComponents = {};

export type AvailableComponents = typeof availableComponents;

export const availableChildComponents: (keyof typeof availableComponents)[] =
  [];

interface Settings {
  label?: string;
  sublabel?: string;
  orLabel?: string;
  emailAddressPlaceholderText?: string;
  passwordPlaceholderText?: string;
  buttonLabel?: string;
  skipButtonLabel?: string;
  noAccountLabel?: string;
}

interface Props {
  settings: Settings;
}

const Login = ({ settings }: Props) => {
  const { retUrl, error } = useQueryParams();
  const { push: navigate } = useNavigateSameTab();
  const [loginError, setLoginError] = useState("");
  const { isEditor, messages, setCurrentMessage } = useContext(EditorCtx);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const errorCodes = useMemo<Record<QueryParams["error"], { text: string }>>(
    () => ({
      INVALID_RESET_PASSWORD_TOKEN: {
        text: t("information_password_token_invalid"),
      },
      SESSION_UNAUTHORIZED: {
        text: t("information_session_timed_out"),
      },
    }),
    [t],
  );

  const onSubmit = async (data: SignInData) => {
    if (isEditor) {
      setCurrentMessage(messages.previewAlert);
      return;
    }
    setLoading(true);
    try {
      await signIn(data).json();
      setLoading(false);
      if (retUrl) {
        const decodedRetUrl = decodeURIComponent(retUrl);
        navigate(`/${decodedRetUrl}`);
      } else {
        navigate("/");
      }
    } catch (e) {
      if (e instanceof ServerError) {
        const errorResponse = e;
        setLoginError(errorResponse.getMessage());
      }
      setLoading(false);
    }
  };

  const errorData = error && errorCodes[error];
  const label = settings?.label || DEFAULT_SIGN_IN_VALUE;

  return (
    <Page>
      <FullHeightContainer>
        <CommonHead titleSufix={label} />
        <Head>
          <meta
            name="description"
            content={t("seo_sign_in_meta_description")}
          />
        </Head>
        <LockScreen>
          <LoginForm
            loading={loading}
            onSubmit={onSubmit}
            serverError={loginError || errorData?.text}
            settings={settings}
          />
        </LockScreen>
      </FullHeightContainer>
    </Page>
  );
};

export default Login;
export type LoginSettings = Settings;
